import { useState } from "react";
import { toast } from "sonner";
import { HelpCircle, Check, X, MinusCircle } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { StatusPill, bonusStatus } from "@/components/StatusPill";

const INJ = { Out: " — absent (blessé)", "Day-To-Day": " — incertain", Questionable: " — incertain", Doubtful: " — très incertain" };
const injLabel = (o) => {
  if (!o.injury) return "";
  const back = o.return_date ? ` (retour ${o.return_date.slice(8)}/${o.return_date.slice(5, 7)})` : "";
  if (o.injury === "Returning") return ` — de retour${back}`;
  return (INJ[o.injury] || " — blessé") + (o.injury === "Out" ? back : "");
};

function Question({ q, game, locked, onSaved }) {
  const [busy, setBusy] = useState(false);
  const save = async (option) => {
    const clear = !option || option === q.my_answer;
    setBusy(true);
    try {
      if (clear) await api.delete(`/advanced/${q.id}`);
      else await api.put(`/advanced/${q.id}`, { option });
      onSaved(q.id, clear ? null : option);
      toast.success(clear ? "Réponse retirée" : "Réponse enregistrée");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const done = q.status === "resolved";
  const ok = done && q.correct_options.includes(q.my_answer);
  return (
    <div className="rounded-xl border border-white/[0.07] bg-[#05070D]/60 p-3" data-testid={`question-${q.id}`}>
      <div className="mb-2 flex items-start justify-between gap-2">
        <p className="text-xs font-semibold text-white">{q.label}</p>
        <span className="flex shrink-0 items-center gap-1.5"><StatusPill s={bonusStatus(q, game)} testId={`bonus-status-${q.id}`} /><span className="font-mono-num text-[11px] text-slate-400">+{q.points_win} / {q.points_lose}</span></span>
      </div>
      {q.options.length <= 4 ? (
        <div className="flex flex-wrap gap-1.5">
          {q.options.map((o) => (
            <button key={o.key} aria-pressed={q.my_answer === o.key} disabled={locked || busy || q.status !== "open"} onClick={() => save(o.key)} data-testid={`question-${q.id}-opt-${o.key}`}
              className={`rounded-full border px-3 py-1.5 text-[11px] font-semibold transition-colors ${q.my_answer === o.key ? "border-[#B7F52A] bg-[#B7F52A] text-[#05070D]" : "border-white/10 text-slate-200 hover:border-white/25"} disabled:cursor-not-allowed`}>
              {o.label}
            </button>
          ))}
        </div>
      ) : (
        <select value={q.my_answer || ""} disabled={locked || busy || q.status !== "open"} onChange={(e) => save(e.target.value)} data-testid={`question-${q.id}-select`}
          className="w-full rounded-lg border border-white/10 bg-[#0B1020] px-2 py-2 text-xs text-white">
          <option value="">{q.my_answer ? "Aucun choix (retirer ma réponse)" : "Choisir un joueur des deux équipes…"}</option>
          {[game.away, game.home].map((t) => {
            const opts = q.options.filter((o) => (o.team || o.label.match(/\(([^)]+)\)$/)?.[1]) === t.abbr);
            return opts.length ? <optgroup key={t.abbr} label={t.name || t.abbr}>{opts.map((o) => <option key={o.key} value={o.key} disabled={o.injury === "Out" && q.my_answer !== o.key} title={o.injury_note || undefined}>{o.label.replace(/ \([^)]+\)$/, "")}{injLabel(o)}</option>)}</optgroup> : null;
          })}
          {q.options.filter((o) => ![game.home.abbr, game.away.abbr].includes(o.team || o.label.match(/\(([^)]+)\)$/)?.[1])).map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
        </select>
      )}
      {q.options.some((o) => o.injury && o.injury !== "Returning") && q.status === "open" && <p className="mt-1.5 text-[10px] text-slate-500" data-testid={`question-${q.id}-injury-note`}>Joueurs blessés signalés par la NBA : les absents ne peuvent pas être choisis.</p>}
      {q.options.find((o) => o.key === q.my_answer && o.injury === "Out") && <p className="mt-1 text-[11px] font-semibold text-amber-300" data-testid={`question-${q.id}-injury-warning`}>Ton joueur est annoncé absent : change ton choix.</p>}
      {q.status === "void" && <p className="mt-2 flex items-center gap-1 text-[11px] text-slate-400"><MinusCircle size={12} /> Question neutralisée ({q.void_reason}) : aucun point</p>}
      {done && q.my_answer && (
        <p className={`mt-2 flex items-center gap-1 text-[11px] font-semibold ${ok ? "text-[#B7F52A]" : "text-red-300"}`}>
          {ok ? <Check size={12} /> : <X size={12} />} {ok ? `Bonne réponse (+${q.points_win})` : `Réponse manquée (${q.points_lose})`}
        </p>
      )}
    </div>
  );
}

export default function AdvancedQuestions({ game, setGames }) {
  const [open, setOpen] = useState(false);
  const qs = game.questions || [];
  if (!qs.length) return null;
  const answered = qs.filter((q) => q.my_answer).length;
  const onSaved = (qid, option) => setGames((gs) => gs.map((g) => (g.id === game.id ? { ...g, questions: g.questions.map((q) => (q.id === qid ? { ...q, my_answer: option } : q)) } : g)));
  return (
    <div className="mt-3 border-t border-white/[0.06] pt-3">
      <button onClick={() => setOpen(!open)} data-testid={`questions-toggle-${game.id}`} className="flex w-full items-center justify-between text-xs font-semibold text-slate-300 hover:text-white">
        <span className="flex items-center gap-1.5"><HelpCircle size={14} className="text-[#B7F52A]" /> Pronostics bonus (facultatifs)</span>
        <span className="font-mono-num text-slate-400">{answered}/{qs.length}</span>
      </button>
      {open && <div className="mt-3 grid gap-2">{qs.map((q) => <Question key={q.id} q={q} game={game} locked={game.locked} onSaved={onSaved} />)}</div>}
    </div>
  );
}
